import { useState, useEffect, useMemo, useCallback } from "react"
import config from "config/app.config"
import { useDynamicCatalog } from "./useDynamicCatalog"

/**
 * V5 CPQ State Hook — Refactored
 *
 * Global config = Kasoma + Pagkos options only.
 * Model/color/pomolo selection is per-section (Wall, Base, Tall).
 */
export const useCpqV5State = () => {
  // Dynamic catalog (swatches + finish groups)
  const {
    swatchesByGroup,
    finishGroupsMap,
    loading: catalogLoading,
    error: catalogError,
  } = useDynamicCatalog()

  // ── Phase: always 1 (building) — config is integrated into the building page ──
  const [phase, setPhase] = useState(1)

  // ── Global Config (quote-level settings) ──
  const [kasoma, setKasoma] = useState("")
  const [pomolo, setPomolo] = useState("")  // "GOLA" | "HANDLE" | ""
  const [model, setModel] = useState("")    // global finish group code
  const [swatch, setSwatch] = useState(null) // global swatch { label, swatchUrl, ... }
  const [countertopType, setCountertopType] = useState("")
  const [sb, setSb] = useState("")

  // ── Per-section config: model, swatch, pomolo ──
  const [sectionConfig, setSectionConfig] = useState({
    Wall: { model: "", swatch: null, pomolo: "" },
    Base: { model: "", swatch: null, pomolo: "" },
    Tall: { model: "", swatch: null, pomolo: "" },
  })

  const updateSectionConfig = useCallback((section, key, value) => {
    setSectionConfig(prev => ({
      ...prev,
      [section]: { ...prev[section], [key]: value },
    }))
  }, [])

  // When global model changes, reset global swatch
  const setGlobalModel = useCallback((m) => {
    setModel(m)
    setSwatch(null)
  }, [])

  // When model changes, reset swatch
  const setSectionModel = useCallback((section, model) => {
    setSectionConfig(prev => ({
      ...prev,
      [section]: { ...prev[section], model, swatch: null },
    }))
  }, [])

  const setSectionSwatch = useCallback((section, swatch) => {
    updateSectionConfig(section, "swatch", swatch)
  }, [updateSectionConfig])

  const setSectionPomolo = useCallback((section, pomolo) => {
    updateSectionConfig(section, "pomolo", pomolo)
  }, [updateSectionConfig])

  // ── Custom Size Surcharge Prices (from DB) ──
  const DEFAULT_SURCHARGES = {
    base_W: 85, base_D: 115, base_H: 115,
    wall_W: 85, wall_D: 115, wall_H: 115,
    tall_W: 140, tall_D: 170, tall_H: 140,
  }
  const [surcharges, setSurcharges] = useState(DEFAULT_SURCHARGES)

  useEffect(() => {
    const fetchSurcharges = async () => {
      try {
        const authUser = JSON.parse(localStorage.getItem("authUser"))
        const token = authUser ? authUser.session_token : ""
        const res = await fetch(config.APP_URL + "_table/custom_size_surcharges", {
          headers: {
            Accept: "application/json",
            "X-Api-Key": config.APP_API_KEY,
            Authorization: token ? `Bearer ${token}` : "",
          },
        })
        const json = await res.json()
        const rows = json.resource || json
        if (Array.isArray(rows) && rows.length > 0) {
          const map = { ...DEFAULT_SURCHARGES }
          rows.forEach(r => {
            const key = `${r.position.toLowerCase()}_${r.dimension}`
            map[key] = parseFloat(r.price) || 0
          })
          setSurcharges(map)
        }
      } catch (err) {
        console.error("[CPQ] Failed to fetch surcharges:", err)
      }
    }
    fetchSurcharges()
  }, [])

  // ── Building Options ──
  const [plaina, setPlaina] = useState("")
  const [plainaDims, setPlainaDims] = useState({ width: "", depth: "", height: "" })
  const [sokolo, setSokolo] = useState("")
  const [pagkosMikos, setPagkosMikos] = useState("")
  const [pagkosVathos, setPagkosVathos] = useState("")
  const [pagkosColor, setPagkosColor] = useState("")

  // ── Inner tabs: 0=ΚΟΥΤΙΑ, 1=ΑΞΕΣΟΥΑΡ, 2=ΕΙΔΙΚΑ, 3=ΠΕΡΙΛΗΨΗ ──
  const [activeTab, setActiveTab] = useState(0)

  // ── Add box dialog ──
  const [addBoxOpen, setAddBoxOpen] = useState(false)
  const [addBoxSection, setAddBoxSection] = useState(null) // "Wall" | "Base" | "Tall" | null
  const [addBoxProductMode, setAddBoxProductMode] = useState("Cabinet") // "Cabinet" | "Accessories" | "Special"
  const [addBoxInitialModel, setAddBoxInitialModel] = useState(null)
  const [addBoxInitialSwatch, setAddBoxInitialSwatch] = useState(null)

  // ── Countertop type options ──
  const countertopOptions = useMemo(() => [
    "ΒΑΚΕΛΙΤΗΣ",
    "ΛΟΥΣΤΡΟ",
    "ΠΛΑΤΗ",
  ], [])

  // ── Readiness ──
  const generalComplete = Boolean(kasoma && pomolo)
  const canStartBuilding = generalComplete

  // ── Open add box with section pre-filtering ──
  // section can be "Wall"/"Base"/"Tall" (cabinet), "Accessories", "Special", "Pagkos", "DoorCladding", or null
  const openAddBoxForSection = useCallback((section) => {
    setAddBoxInitialModel(null)
    setAddBoxInitialSwatch(null)
    if (section === "Accessories") {
      setAddBoxSection(null)
      setAddBoxProductMode("Accessories")
    } else if (section === "Special") {
      setAddBoxSection(null)
      setAddBoxProductMode("Special")
    } else if (section === "Pagkos") {
      setAddBoxSection(null)
      setAddBoxProductMode("Pagkos")
    } else if (section === "DoorCladding") {
      setAddBoxSection(null)
      setAddBoxProductMode("DoorCladding")
    } else {
      setAddBoxSection(section)
      setAddBoxProductMode("Cabinet")
    }
    setAddBoxOpen(true)
  }, [])

  // ── Open add box seeded with a specific item's model/swatch ──
  const openAddBoxForItem = useCallback((section, modelCode, swatch) => {
    setAddBoxSection(section)
    setAddBoxProductMode("Cabinet")
    setAddBoxInitialModel(modelCode || null)
    setAddBoxInitialSwatch(swatch || null)
    setAddBoxOpen(true)
  }, [])

  // ── Preview modal flag ──
  const [openPreviewOnBuild, setOpenPreviewOnBuild] = useState(false)

  // ── Phase transitions ──
  const startBuilding = useCallback(() => {
    setPhase(1)
    setActiveTab(0)
  }, [])

  const startBuildingWithPreview = useCallback(() => {
    setPhase(1)
    setActiveTab(0)
    setOpenPreviewOnBuild(true)
  }, [])

  const backToConfig = useCallback(() => {
    setPhase(0)
  }, [])

  // ── Settings chips for header display ──
  const settingsChips = useMemo(() => {
    const chips = []
    if (kasoma) chips.push({ label: `Κασωμα: ${kasoma}` })
    if (pomolo) chips.push({ label: pomolo })
    if (model) chips.push({ label: `Μοντελο: ${model}` })
    if (swatch) chips.push({ label: `Χρωμα: ${swatch.label}` })
    if (sb) chips.push({ label: `Σ/Β: ${sb}` })
    if (countertopType) chips.push({ label: `Παγκος: ${countertopType}` })
    // Per-section summaries (if overridden)
    for (const [key, cfg] of Object.entries(sectionConfig)) {
      if (cfg.model) chips.push({ label: `${key}: ${cfg.model}` })
      if (cfg.swatch) chips.push({ label: `${key} Χρ: ${cfg.swatch.label}` })
      if (cfg.pomolo) chips.push({ label: `${key} Πομ: ${cfg.pomolo}` })
    }
    if (plaina) {
      let dimStr = plaina
      const parts = []
      if (plainaDims.width) parts.push(`Π:${plainaDims.width}`)
      if (plainaDims.depth) parts.push(`Β:${plainaDims.depth}`)
      if (plainaDims.height) parts.push(`Υ:${plainaDims.height}`)
      if (parts.length) dimStr += ` (${parts.join(" × ")})`
      chips.push({ label: `Πλαινα: ${dimStr}` })
    }
    if (sokolo) chips.push({ label: `Σοκολο: ${sokolo}` })
    if (pagkosMikos) chips.push({ label: `Μηκος: ${pagkosMikos}` })
    return chips
  }, [kasoma, pomolo, model, swatch, sb, countertopType, sectionConfig, plaina, plainaDims, sokolo, pagkosMikos])

  // ── Compatibility bridge for shared components ──
  const compatBridge = useMemo(() => ({
    kasoma,
    setKasoma,
    sb,
    setSb,
    countertopType,
    setCountertopType,
    countertopOptions,
    activeTab,
    setActiveTab,
    addBoxOpen,
    setAddBoxOpen,
    openAddBoxForSection,
    settingsExpanded: false,
    setSettingsExpanded: () => {},
    settingsChips,
    pomolo, setPomolo,
    model, setGlobalModel,
    swatch, setSwatch,
    hasSettings: Boolean(kasoma || pomolo || countertopType),
    catalogLoading,
    catalogError,
    openPreviewOnBuild,
    setOpenPreviewOnBuild,
    swatchesByGroup,
    finishGroupsMap,
  }), [
    kasoma, pomolo, model, swatch, sb, countertopType, countertopOptions,
    activeTab, addBoxOpen, settingsChips,
    catalogLoading, catalogError,
    openPreviewOnBuild, swatchesByGroup, finishGroupsMap,
  ])

  // ── Restore full config from a saved quote ──
  const restoreConfig = useCallback((cfg) => {
    setKasoma(cfg.kasoma || "")
    setPomolo(cfg.pomolo || "")
    setModel(cfg.model || "")
    setSwatch(cfg.swatch || null)
    setSb(cfg.sb || "")
    setCountertopType(cfg.countertopType || "")
    setPlaina(cfg.plaina || "")
    setPlainaDims(cfg.plainaDims || { width: "", depth: "", height: "" })
    setSokolo(cfg.sokolo || "")
    setPagkosMikos(cfg.pagkosMikos || "")
    setPagkosVathos(cfg.pagkosVathos || "")
    setPagkosColor(cfg.pagkosColor || "")
    setSectionConfig(cfg.sectionConfig || {
      Wall: { model: "", swatch: null, pomolo: "" },
      Base: { model: "", swatch: null, pomolo: "" },
      Tall: { model: "", swatch: null, pomolo: "" },
    })
  }, [])

  // ── Serialize current config for saving ──
  const getConfigSnapshot = useCallback(() => ({
    kasoma, pomolo, model, swatch, sb, countertopType,
    plaina, plainaDims, sokolo, pagkosMikos, pagkosVathos, pagkosColor,
    sectionConfig,
  }), [kasoma, pomolo, model, swatch, sb, countertopType, plaina, plainaDims, sokolo, pagkosMikos, pagkosVathos, pagkosColor, sectionConfig])

  return {
    // Dynamic catalog
    swatchesByGroup,
    finishGroupsMap,
    catalogLoading,
    catalogError,

    // Phase
    phase,
    setPhase,
    startBuilding,
    startBuildingWithPreview,
    backToConfig,

    // Global config
    countertopType,
    setCountertopType,
    countertopOptions,
    kasoma,
    setKasoma,
    pomolo,
    setPomolo,
    model,
    setGlobalModel,
    swatch,
    setSwatch,
    sb,
    setSb,
    generalComplete,

    // Per-section config
    sectionConfig,
    setSectionModel,
    setSectionSwatch,
    setSectionPomolo,

    // Building options
    plaina,
    setPlaina,
    plainaDims,
    setPlainaDims,
    sokolo,
    setSokolo,
    pagkosMikos,
    setPagkosMikos,
    pagkosVathos,
    setPagkosVathos,
    pagkosColor,
    setPagkosColor,

    // Readiness
    canStartBuilding,

    // Inner tabs
    activeTab,
    setActiveTab,

    // Add box
    addBoxOpen,
    setAddBoxOpen,
    addBoxSection,
    setAddBoxSection,
    addBoxProductMode,
    setAddBoxProductMode,
    openAddBoxForSection,
    openAddBoxForItem,
    addBoxInitialModel,
    addBoxInitialSwatch,

    // Summary
    settingsChips,

    // Compatibility bridge
    compatBridge,

    // Quote persistence
    restoreConfig,
    getConfigSnapshot,

    // Custom size surcharges (from DB)
    surcharges,
  }
}
